import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type RunCeilings, type SecretSummary, type WorkflowSummary } from '../api.ts';
import { ShareLinksCard } from './ShareLinksCard.tsx';
import { SlackSettings } from './SlackSettings.tsx';

/**
 * The two controls that belong to the deployment rather than to any workflow.
 *
 * **Run ceilings** are a safety control, not a report. `StepLimits` bounds one
 * step and always did; these bound the deployment, which is the only place a
 * step that spawns steps that spawn steps is visible. The current usage sits
 * next to each ceiling because "is 50 the right number" is not answerable
 * without today's figure beside it.
 *
 * **Secrets** are the values a connector needs that no `wrangler secret put`
 * can supply, because they are set by an admin in a browser. Names and scopes
 * are listed; values are not, and there is no endpoint that would return one.
 *
 * **Share links** are the fourth, and the one with teeth. A share token is the
 * only credential in reef that is not a session — `/shared/:token` sits outside
 * the auth middleware on purpose — and it could be minted from the Artifacts
 * screen with no way to see or withdraw what was outstanding.
 *
 * **Slack** is the third, and it is here rather than on the rail for two
 * reasons. It is one integration, and a top-level destination beside
 * Connections and Policies would say it is a peer of those. And what it
 * actually holds — which channels the deployment posts every plan into, and who
 * asked for that — is a fact about the deployment's blast radius, which is what
 * the other two sections are about too.
 */
type Section = 'ceilings' | 'secrets' | 'slack' | 'share-links';

export function AdminView({ isAdmin }: { isAdmin: boolean }) {
  const [section, setSection] = useState<Section>('ceilings');
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  // Slack and share links are genuinely usable by a non-admin — they act on
  // workflows that person already holds `edit` on, and on their own Slack
  // link — where every endpoint behind Secrets refuses them outright.
  const openToEveryone: Section[] = ['ceilings', 'slack', 'share-links'];
  const showing: Section = isAdmin || openToEveryone.includes(section) ? section : 'ceilings';

  useEffect(() => {
    // For the workflow picker on the Slack section. Scoped by the control plane
    // to what this person can see, so a non-admin binding a channel can only
    // name a workflow they hold `edit` on anyway.
    void api
      .listWorkflows()
      .then((result) => setWorkflows(result.workflows))
      .catch(() => setWorkflows([]));
  }, []);

  return (
    <div className="body">
      <aside className="sidebar">
        <p className="section-title">Deployment</p>
        <button
          className="list-item"
          aria-current={section === 'ceilings'}
          onClick={() => setSection('ceilings')}
        >
          Run ceilings
          <small>What this deployment will allow at all</small>
        </button>
        {/*
          Not offered to a non-admin at all. Every endpoint behind it refuses
          them, and a tab that only ever produces a 403 is worse than an absent
          one — the ceilings above are genuinely readable, so the asymmetry is
          the honest thing to show.
        */}
        {isAdmin && (
          <button
            className="list-item"
            aria-current={section === 'secrets'}
            onClick={() => setSection('secrets')}
          >
            Secrets
            <small>Values a connection can use, encrypted</small>
          </button>
        )}
        {/*
          Offered to everybody, unlike Secrets. A non-admin can genuinely act
          here — binding a channel to a workflow they hold `edit` on is theirs
          to do — and the read half is how anybody finds out where their
          workflow's reviews are going.
        */}
        <button
          className="list-item"
          aria-current={section === 'slack'}
          onClick={() => setSection('slack')}
        >
          Slack
          <small>Which channels reef posts to</small>
        </button>
        <button
          className="list-item"
          aria-current={section === 'share-links'}
          onClick={() => setSection('share-links')}
        >
          Share links
          <small>Read-only links that work without a sign-in</small>
        </button>
      </aside>

      <main className="main">
        {!isAdmin && showing === 'ceilings' && (
          <div className="banner warn">
            <h3>Read-only</h3>
            <p style={{ margin: 0 }}>
              You can see the deployment's ceilings but not change them. Both of these are managed
              by the <code>reef-admins</code> group.
            </p>
          </div>
        )}
        {showing === 'ceilings' && <CeilingsCard isAdmin={isAdmin} />}
        {showing === 'secrets' && <SecretsCard />}
        {showing === 'slack' && <SlackSettings isAdmin={isAdmin} workflows={workflows} />}
        {showing === 'share-links' && <ShareLinksCard />}
      </main>
    </div>
  );
}

type NumericCeiling =
  | 'maxConcurrentRuns'
  | 'maxDailySpendCents'
  | 'maxStepDepth'
  | 'maxChildrenPerStep';

function CeilingsCard({ isAdmin }: { isAdmin: boolean }) {
  const [ceilings, setCeilings] = useState<RunCeilings | null>(null);
  const [usage, setUsage] = useState<{ activeRuns: number; dailySpendCents: number } | null>(null);
  const [admitting, setAdmitting] = useState(true);
  const [reason, setReason] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<RunCeilings>>({});
  const [note, setNote] = useState<string | null>(null);

  /*
   * Which read the screen is showing. Saving triggers a read of its own, and if
   * an earlier one is still in flight the two can land out of order — the older
   * response then paints the pre-save numbers back over the saved ones *and*
   * clears the draft, so a change that was written to D1 reads on screen as a
   * change that did not take.
   */
  const latest = useRef(0);

  const refresh = useCallback(() => {
    const request = ++latest.current;
    api
      .runCeilings()
      .then((result) => {
        if (request !== latest.current) return;
        setCeilings(result.ceilings);
        setUsage(result.usage);
        setAdmitting(result.admitting);
        setReason(result.reason);
        setDraft({});
      })
      .catch((error) => request === latest.current && setNote((error as Error).message));
  }, []);

  useEffect(refresh, [refresh]);

  if (!ceilings || !usage) return <div className="card">Loading ceilings…</div>;

  const value = (key: NumericCeiling): number => draft[key] ?? ceilings[key];
  const dirty = Object.keys(draft).length > 0;

  return (
    <>
      <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>Run ceilings</h1>
      <p className="hint" style={{ marginTop: 0 }}>
        What this deployment will allow, whatever any single workflow says. A run that would exceed
        one of these is refused before it exists.
      </p>

      {!admitting && (
        <div className="banner danger">
          <h3>Not admitting runs</h3>
          <p style={{ margin: 0 }}>{reason}</p>
        </div>
      )}
      {note && (
        <div className="banner">
          <p style={{ margin: 0 }}>{note}</p>
        </div>
      )}

      <div className="card">
        <h2>Ceilings</h2>
        <div className="stack">
          <Ceiling
            id="max-concurrent"
            label="Runs in flight at once"
            help="Counts runs that are pending or running, across every workflow."
            now={`${usage.activeRuns} running now`}
            atCeiling={usage.activeRuns >= ceilings.maxConcurrentRuns}
            value={value('maxConcurrentRuns')}
            editable={isAdmin}
            onChange={(next) => setDraft({ ...draft, maxConcurrentRuns: next })}
          />
          <Ceiling
            id="max-spend"
            label="Inference spend, rolling 24h"
            help="In cents. A rolling window rather than a calendar day, because a midnight reset is a spike waiting to happen."
            now={`$${(usage.dailySpendCents / 100).toFixed(2)} spent in the last 24h`}
            atCeiling={usage.dailySpendCents >= ceilings.maxDailySpendCents}
            value={value('maxDailySpendCents')}
            editable={isAdmin}
            onChange={(next) => setDraft({ ...draft, maxDailySpendCents: next })}
          />
          <Ceiling
            id="max-depth"
            label="Step depth"
            help="How deep nesting may go, checked where a parent hands work to a child. Can only tighten — the builder refuses a fourth level regardless."
            value={value('maxStepDepth')}
            editable={isAdmin}
            onChange={(next) => setDraft({ ...draft, maxStepDepth: next })}
          />
          <Ceiling
            id="max-children"
            label="Child steps per run"
            help="The fan-out ceiling. Counted from the step runs already recorded, so a retried parent does not get a fresh allowance."
            value={value('maxChildrenPerStep')}
            editable={isAdmin}
            onChange={(next) => setDraft({ ...draft, maxChildrenPerStep: next })}
          />
        </div>

        <p className="hint">
          {/*
            The migration seeds these with its own name and the epoch, which
            would otherwise render as "changed by migration on 12/31/1969" — a
            date that reads as a bug rather than as "nobody has touched this".
          */}
          {!ceilings.updatedBy || ceilings.updatedBy === 'migration'
            ? 'Still the defaults — nobody has changed these on this deployment.'
            : `Last changed by ${ceilings.updatedBy}${
                ceilings.updatedAt ? ` on ${new Date(ceilings.updatedAt).toLocaleString()}` : ''
              }.`}
        </p>

        <div className="row">
          {isAdmin && (
            <button
              className="primary"
              disabled={!dirty}
              onClick={async () => {
                setNote(null);
                try {
                  await api.saveRunCeilings(draft);
                  refresh();
                } catch (error) {
                  setNote((error as Error).message);
                }
              }}
            >
              Save ceilings
            </button>
          )}
          {dirty && <button onClick={() => setDraft({})}>Discard</button>}
          <button onClick={refresh}>Refresh</button>
        </div>
      </div>
    </>
  );
}

/**
 * One ceiling, with what it is currently doing beside it.
 *
 * A `.field` rather than a table row: the `th` style is uppercase and
 * letter-spaced, which is right for a column header and wrong for a sentence of
 * help — the first version of this screen shouted every explanation.
 */
function Ceiling({
  id,
  label,
  help,
  now,
  atCeiling,
  value,
  editable,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  now?: string;
  atCeiling?: boolean;
  value: number;
  editable: boolean;
  onChange(next: number): void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="row wrap">
        {editable ? (
          <input
            id={id}
            type="number"
            min={1}
            value={value}
            style={{ width: '8rem' }}
            onChange={(event) => onChange(Number.parseInt(event.target.value, 10) || 1)}
          />
        ) : (
          <span className="pill">{value}</span>
        )}
        {now && <span className={atCeiling ? 'pill warn' : 'pill'}>{now}</span>}
      </div>
      <span className="hint">{help}</span>
    </div>
  );
}

function SecretsCard() {
  const [secrets, setSecrets] = useState<SecretSummary[]>([]);
  const [scope, setScope] = useState('workspace');
  const [scopeId, setScopeId] = useState('');
  const [block, setBlock] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api
      .listSecrets()
      .then((result) => setSecrets(result.secrets))
      .catch((error) => setNote((error as Error).message));
  }, []);

  useEffect(refresh, [refresh]);

  return (
    <>
      <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>Secrets</h1>
      <p className="hint" style={{ marginTop: 0 }}>
        Encrypted values a connection can use without anybody pasting them into a workflow. A Worker
        secret of the same name always wins — anything an admin ran <code>wrangler secret put</code>{' '}
        for is the more deliberate of the two. Reef never shows a value back, including here.
      </p>

      {note && (
        <div className="banner">
          <p style={{ margin: 0 }}>{note}</p>
        </div>
      )}

      <div className="card">
        <h2>Stored</h2>
        {secrets.length === 0 ? (
          <p className="hint" style={{ margin: 0 }}>
            None stored yet.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Scope</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {secrets.map((secret) => (
                <tr key={`${secret.scope}:${secret.scopeId}:${secret.key}`}>
                  <td>
                    <code>{secret.key}</code>
                  </td>
                  <td>
                    {secret.scope}
                    {secret.scopeId ? ` · ${secret.scopeId}` : ''}
                  </td>
                  <td>{new Date(secret.updatedAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={async () => {
                        await api.deleteSecret(secret.scope, secret.scopeId, secret.key);
                        refresh();
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>Add</h2>
        <div className="row wrap">
          <div className="field">
            <label htmlFor="secret-scope">Scope</label>
            <select
              id="secret-scope"
              value={scope}
              onChange={(event) => setScope(event.target.value)}
            >
              <option value="workspace">workspace</option>
              <option value="connection">connection</option>
              <option value="repo">repo</option>
              <option value="environment">environment</option>
              <option value="workflow">workflow</option>
            </select>
          </div>
          {scope !== 'workspace' && (
            <div className="field">
              <label htmlFor="secret-scope-id">Which one</label>
              <input
                id="secret-scope-id"
                value={scopeId}
                placeholder="clickhouse-prod"
                onChange={(event) => setScopeId(event.target.value)}
              />
            </div>
          )}
        </div>

        <div className="field">
          <label htmlFor="secret-env">Paste an .env block</label>
          <textarea
            id="secret-env"
            rows={4}
            value={block}
            spellCheck={false}
            style={{ width: '100%', fontFamily: 'ui-monospace, monospace' }}
            placeholder={'CLICKHOUSE_PASSWORD=…\nexport OTHER_KEY="…"'}
            onChange={(event) => setBlock(event.target.value)}
          />
          <span className="hint">
            One <code>KEY=value</code> per line. <code>export</code> and quotes are tolerated,
            because that is what people actually paste.
          </span>
        </div>

        <div className="row">
          <button
            className="primary"
            disabled={block.trim().length === 0}
            onClick={async () => {
              setNote(null);
              try {
                const result = await api.putSecrets({
                  scope,
                  scopeId: scope === 'workspace' ? null : scopeId || null,
                  env: block,
                });
                setBlock('');
                setNote(`Stored ${result.stored.join(', ')}.`);
                refresh();
              } catch (error) {
                setNote((error as Error).message);
              }
            }}
          >
            Store
          </button>
        </div>
      </div>
    </>
  );
}
